import { imageUrl, type Post, type RenderedStoryBlock } from '../../api/posts';
import { DashboardBlock } from '../dashboards/DashboardBlock';
import { Alert } from '../ui';
import './StoryBody.css';

export interface StoryBodyProps {
  post: Post;
  /** The rendered figures, keyed by block id. Absent while the story is loading. */
  rendered?: Record<string, RenderedStoryBlock>;
}

/**
 * A story, read.
 *
 * The rule this component exists to keep is the specification's: **the story
 * must be understandable without interaction.** So a figure is shown with the
 * sentence its author wrote beside it, the numbers behind every chart are on
 * the page rather than behind a hover, and nothing here can be clicked to
 * reveal something the reader would otherwise miss.
 */
/**
 * What went wrong with a figure, as a heading.
 *
 * A figure the leakage guard would not answer is not a broken figure, and
 * saying so would tell the reader the wrong thing about the story: the numbers
 * exist, they are just not something a public page may show about data that is
 * not public (Phase 15).
 */
const GUARDED = new Set([
  'ROW_LEVEL_NOT_ALLOWED',
  'REVEALING_AGGREGATION',
  'IDENTIFYING_DIMENSION',
  'TOO_MANY_DIMENSIONS',
  'TOO_MANY_FILTERS',
  'LEAKAGE_REFUSED',
]);

function figureProblem(code: string): string {
  return GUARDED.has(code) ? 'This figure is not public' : 'This figure is no longer available';
}

export function StoryBody({ post, rendered }: StoryBodyProps) {
  return (
    <article className="sc-story">
      <header className="sc-story__head">
        <h1 className="sc-story__title">{post.title}</h1>
        {post.subtitle && <p className="sc-story__subtitle">{post.subtitle}</p>}
      </header>

      {post.blocks.map((block) => {
        const state = rendered?.[block.id];

        switch (block.type) {
          case 'title':
            return (
              <h2 key={block.id} className="sc-story__heading">
                {block.content.text}
              </h2>
            );

          case 'subtitle':
            return (
              <h3 key={block.id} className="sc-story__subheading">
                {block.content.text}
              </h3>
            );

          case 'rich_text':
            return (
              <p key={block.id} className="sc-story__prose">
                {block.content.text}
              </p>
            );

          case 'quote':
            return (
              <blockquote key={block.id} className="sc-story__quote">
                <p>{block.content.text}</p>
                {block.content.attribution && <cite>{block.content.attribution}</cite>}
              </blockquote>
            );

          case 'divider':
            return <hr key={block.id} className="sc-story__divider" />;

          case 'image':
            return (
              <figure key={block.id} className="sc-story__figure">
                <img
                  src={imageUrl(post.id, block.content.image ?? '')}
                  alt={block.content.alt ?? ''}
                  className="sc-story__image"
                />
                {block.content.caption && <figcaption>{block.content.caption}</figcaption>}
              </figure>
            );

          default:
            return (
              <figure key={block.id} className="sc-story__figure">
                {state?.error && (
                  <Alert tone="warning" title={figureProblem(state.error.code)}>
                    {state.error.message}
                  </Alert>
                )}

                {state?.block && (
                  <DashboardBlock block={state.block} state={state.state} titleAs="h3" />
                )}

                {state?.blocks?.map((entry) => (
                  <DashboardBlock
                    key={entry.block.id}
                    block={entry.block}
                    state={entry.state}
                    titleAs="h3"
                  />
                ))}

                {!state && <p className="sc-story__pending">Loading this figure…</p>}

                {block.content.caption && <figcaption>{block.content.caption}</figcaption>}
              </figure>
            );
        }
      })}
    </article>
  );
}
